import React, { useEffect, useState } from 'react';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { useDispatch } from 'react-redux';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import styles from './styles.module.scss';
import { Helmet } from 'react-helmet';
import SimpleTabs from '../../common/tabs/SimpleTabs';
import StyledTab from '../../common/tabs/SimpleTab';
import { a11yProps } from '../../../helpers/utils';
import classNames from 'classnames';
import SwipeableViews from 'react-swipeable-views';
import TabPanel from '../../common/tabs/SimpleTabPanel';
import { toast } from 'react-toastify';
import Loader from '../../common/Loader';
import SurveyCard from '../../common/cards/SurveyCard';
import Eof from '../../common/Eof';
import { useInfiniteQuery } from '@tanstack/react-query';
import { surveysListRequest } from './api';
import Button from '@mui/material/Button';
import { useTranslation } from '../../../i18n/LanguageProvider';

const SurveyList = () => {
  const { t, isRtl } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();
  const [tabIndex, setTabIndex] = useState(0);

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));

  const {
    isLoading: surveysActiveIsLoading,
    data: surveysActiveData,
    hasNextPage: surveysActiveHasNextPage,
    fetchNextPage: surveysActiveFetchNextPage,
    isFetchingNextPage: surveysActiveIsFetchingNextPage
  } = useInfiniteQuery(
    ['surveysActiveList'],
    ({ pageParam = null }) => surveysListRequest(pageParam, false),
    {
      getNextPageParam: (lastPage) => {
        const lastIndex = lastPage.data.length - 1;
        const lastId = lastPage.data[lastIndex]?.id;
        return lastPage.data.length !== 0 ? lastId : undefined;
      },
      select: React.useCallback(
        (data) => data?.pages.flatMap((feedback) => feedback.data),
        []
      ),
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const {
    isLoading: surveysEndedIsLoading,
    data: surveysEndedData,
    hasNextPage: surveysEndedHasNextPage,
    fetchNextPage: surveysEndedFetchNextPage,
    isFetchingNextPage: surveysEndedIsFetchingNextPage
  } = useInfiniteQuery(
    ['surveysEndedList'],
    ({ pageParam = null }) => surveysListRequest(pageParam, true),
    {
      getNextPageParam: (lastPage) => {
        const lastIndex = lastPage.data.length - 1;
        const lastId = lastPage.data[lastIndex]?.id;
        return lastPage.data.length !== 0 ? lastId : undefined;
      },
      select: React.useCallback(
        (data) => data?.pages.flatMap((feedback) => feedback.data),
        []
      ),
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const handleChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleChangeIndex = (index) => {
    setTabIndex(index);
  };

  useEffect(() => {
    setHeader(t('survey.listTitle'));
    setHeaderConfigs({ credits: false, exit: false, back: true, logo: false });
  }, []);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{t('survey.listTitle')}</title>
      </Helmet>
      {isDesktop && (
        <div className={styles.pageHeader}>
          <span>{t('survey.listTitle')}</span>
        </div>
      )}
      <div className={styles.section}>
        <div className={styles.sectionTop}>
          <SimpleTabs value={tabIndex} handleChange={handleChange}>
            <StyledTab label={t('survey.active')} {...a11yProps(0)} />
            <StyledTab label={t('survey.finished')} {...a11yProps(1)} />
          </SimpleTabs>
        </div>
        <div className={classNames(styles.sectionBody, styles.noMargin)}>
          <SwipeableViews
            axis={isRtl ? 'x-reverse' : 'x'}
            index={tabIndex}
            onChangeIndex={handleChangeIndex}
            className={styles.swipeArea}
          >
            <TabPanel index={0} value={tabIndex}>
              {surveysActiveIsLoading ? (
                <Loader size={40} />
              ) : surveysActiveData.length ? (
                surveysActiveData.map((survey, i) => (
                  <SurveyCard key={i} data={survey} uneditable={false} />
                ))
              ) : (
                <div className={classNames(styles.emptyCard, styles.survey)}>
                  {t('survey.emptyActive')}
                </div>
              )}

              {!surveysActiveIsLoading &&
                (surveysActiveIsFetchingNextPage ? (
                  <Loader size={40} />
                ) : surveysActiveHasNextPage ? (
                  <Button
                    variant="outlined"
                    onClick={surveysActiveFetchNextPage}
                    fullWidth
                  >
                    {t('common.more')}
                  </Button>
                ) : (
                  <Eof />
                ))}
            </TabPanel>
            <TabPanel index={1} value={tabIndex}>
              {surveysEndedIsLoading ? (
                <Loader size={40} />
              ) : surveysEndedData.length ? (
                surveysEndedData.map((survey, i) => (
                  <SurveyCard
                    uneditable={true}
                    key={i}
                    extraClass={styles.surveyCard}
                    data={survey}
                  />
                ))
              ) : (
                <div className={classNames(styles.emptyCard, styles.survey)}>
                  {t('survey.empty')}
                </div>
              )}
              {!surveysEndedIsLoading &&
                (surveysEndedIsFetchingNextPage ? (
                  <Loader size={40} />
                ) : surveysEndedHasNextPage ? (
                  <Button
                    variant="outlined"
                    onClick={surveysEndedFetchNextPage}
                    fullWidth
                  >
                    {t('common.more')}
                  </Button>
                ) : (
                  <Eof />
                ))}
            </TabPanel>
          </SwipeableViews>
        </div>
      </div>
    </div>
  );
};

export default SurveyList;
